import {useFormikContext} from 'formik'
import {useCallback, useEffect, useRef} from 'react'

import {PromoApplied, PromoError} from '../../api/promo-codes/types'

export type PromoFormValues = {
  promoCode?: string | null
  // Last rejection for the code the customer typed (quote promoError or a pay-time 422).
  promoRejection?: PromoError | null
}

const NOT_APPLICABLE: PromoError = {code: 'PROMO_NOT_APPLICABLE'}

// Wires a Formik booking form to PromoCodeField. A code the quote rejects is taken off the
// booking (so it is never sent with the draft) but the message stays under the field.
// `quotedAt` (react-query dataUpdatedAt) makes sure only a quote asked AFTER the code was applied
// is judged — the quote cached from before still has no promo in it.
export const useFormikPromoCode = ({
  promoApplied,
  promoError,
  isQuoting,
  quotedAt,
}: {
  promoApplied?: PromoApplied | null
  promoError?: PromoError | null
  isQuoting: boolean
  quotedAt: number
}) => {
  const {values, setFieldValue} = useFormikContext<PromoFormValues>()
  const appliedAt = useRef(0)

  useEffect(() => {
    if (isQuoting || !values.promoCode || quotedAt <= appliedAt.current || promoApplied) {
      return
    }
    void setFieldValue('promoRejection', promoError || NOT_APPLICABLE)
    void setFieldValue('promoCode', null)
  }, [promoApplied, promoError, isQuoting, quotedAt])

  const onApply = useCallback(
    (code: string) => {
      appliedAt.current = Date.now()
      void setFieldValue('promoRejection', null)
      void setFieldValue('promoCode', code)
    },
    [setFieldValue],
  )

  const onRemove = useCallback(() => {
    void setFieldValue('promoRejection', null)
    void setFieldValue('promoCode', null)
  }, [setFieldValue])

  return {
    code: values.promoCode || null,
    rejection: values.promoRejection || null,
    onApply,
    onRemove,
  }
}
